import _get from 'lodash/get';
import { isMobileLayout } from 'services/utils';
import { getProductDescription, GOOD_CELLULAR_CONNECTION_THRESHOLD, LABEL_KEY_ALERTS_DISABLED, isReplaced } from 'services/SensorHelper'
import { TRANSFER_DEVICE, UPDATE_DEVICE } from 'services/Permissions';
import { States } from '../../../../app.router';
import {
    BATTERY_STATE_SEVERITY,
    getBatteryStateLabel,
    getBatteryIcon,
    getBatteryStateBadgeClass,
    getBatteryInfoHintClass,
    getBatteryPercentage,
    getBatteryTooltipSentence,
    getEstimatedRemainingYears,
    getEstimatedRemainingYearsDisplay,
} from 'services/BatteryHelper';

function getThingColorClass(thing) {
    let colorClass = '';
    if (thing.alertsStatus) {
        for (let i = 0; i < thing.alertsStatus.firingAlerts.length; i++) {
            const alert = thing.alertsStatus.firingAlerts[i];
            if (!alert.muted) {
                if (alert.severity === 'ERROR') {
                    return 'alert';
                }
                colorClass = 'warning';
            }
        }
    }
    return colorClass;
}

/* @ngInject */
export default class ThingsListController {
    constructor(EventEmitter, DialogService, SensorService, $state, $scope, RoleManager, ToastService, FeatureFlags) {
        this.eventEmitter = EventEmitter;
        this.DialogService = DialogService;
        this.$state = $state;
        this.$scope = $scope;
        this.RoleManager = RoleManager;
        this.SensorService = SensorService;
        this.ToastService = ToastService;
        this.FeatureFlags = FeatureFlags;

        this.onReorder = this.onReorder.bind(this);
        this.onChange = this.onChange.bind(this);
        this.getThingColorClass = getThingColorClass;
        this.selectionHasCCON = false;
    }

    get canTransferSensors() {
        return this.RoleManager.can(TRANSFER_DEVICE);
    }

    get canUpdate() {
        return this.RoleManager.can(UPDATE_DEVICE);
    }

    get goodCellularConnectionThreshold() { // eslint-disable-line class-methods-use-this
        return GOOD_CELLULAR_CONNECTION_THRESHOLD;
    }

    get isRowSelectible() {
        return this.$state.is(States.SENSORS) && !isMobileLayout();
    }

    productDescription(device) { // eslint-disable-line class-methods-use-this
        return getProductDescription(device)
    }

    degradedEstimate(device) { // eslint-disable-line class-methods-use-this
        return device.reported.deskOccupancy?.remarks?.includes('INCOMPLETE_DATA')
    }

    alertsDisabled(device) { // eslint-disable-line class-methods-use-this
        return device.labels && device.labels[LABEL_KEY_ALERTS_DISABLED] !== undefined
    }

    isReplaced(device) { // eslint-disable-line class-methods-use-this
        return isReplaced(device);
    }

    isActive(id) {
        return this.$state.params.sensorId === id;
    }

    getBatteryStateLabel(thing) { return getBatteryStateLabel(thing); } // eslint-disable-line class-methods-use-this

    getBatteryStateBadgeClass(thing) { return getBatteryStateBadgeClass(thing); } // eslint-disable-line class-methods-use-this

    getBatteryInfoHintClass(thing) { return getBatteryInfoHintClass(thing); } // eslint-disable-line class-methods-use-this

    getBatteryIcon(thing) { return getBatteryIcon(thing); } // eslint-disable-line class-methods-use-this

    getBatteryTooltipSentence(thing) { return getBatteryTooltipSentence(thing); } // eslint-disable-line class-methods-use-this

    getEstimatedRemainingYears(thing) { return getEstimatedRemainingYears(thing); } // eslint-disable-line class-methods-use-this

    getEstimatedRemainingYearsDisplay(thing) { return getEstimatedRemainingYearsDisplay(thing); } // eslint-disable-line class-methods-use-this

    getBatteryPercentage(thing) { return getBatteryPercentage(thing); } // eslint-disable-line class-methods-use-this

    $onInit() {
        this.allSelected = false;
        this.lastServerOrder = this.order;
    }

    onReorder(order) {
        const BATTERY_SORT_FIELD = 'reported.batteryStatus.estimatedRemainingYears';
        if (order === BATTERY_SORT_FIELD || order === `-${BATTERY_SORT_FIELD}`) {
            const descending = order.startsWith('-');
            this.things.sort((a, b) => {
                const stateA = _get(a, 'reported.batteryStatus.state', 'UNKNOWN');
                const stateB = _get(b, 'reported.batteryStatus.state', 'UNKNOWN');
                const severityA = BATTERY_STATE_SEVERITY[stateA] !== undefined ? BATTERY_STATE_SEVERITY[stateA] : 3;
                const severityB = BATTERY_STATE_SEVERITY[stateB] !== undefined ? BATTERY_STATE_SEVERITY[stateB] : 3;
                if (severityA !== severityB) {
                    return descending ? severityB - severityA : severityA - severityB;
                }
                const yearsA = _get(a, 'reported.batteryStatus.estimatedRemainingYears', Infinity);
                const yearsB = _get(b, 'reported.batteryStatus.estimatedRemainingYears', Infinity);
                return descending ? yearsB - yearsA : yearsA - yearsB;
            });
            // Restore the last server-side order so the two-way binding doesn't
            // propagate the battery field to the parent and break page navigation.
            this.order = this.lastServerOrder;
            return;
        }
        this.lastServerOrder = order;
        this.onListReorder({
            order
        });
    }

    onChange() {
        
        // There is a limit of 100 devices on batch opeartions (setting labels or moving devices)
        if (this.selected.length > 100) {
            this.selected.splice(100)
            this.$scope.$applyAsync()
            this.ToastService.showSimpleTranslated('sensor_batch_max_limit');
            return
        }

        this.onSelectionChange(
            this.eventEmitter({
                selected: this.selected
            })
        );

        // Check if selection has CCON, used to disable 'Add to Dashboard' button
        // Device will always be in cache
        this.SensorService.getFromCache(this.selected).then(({devices}) => {
            this.selectionHasCCON = !!devices.find(device => device.type === 'ccon')
        })
    }

    transferSensors(event) {
        this.onTransferSensors(
            this.eventEmitter({
                event,
                sensors: this.selected
            })
        );
    }

    addSensorsToDashboard(event) {
        this.onAddSensorsToDashboard(
            this.eventEmitter({
                event,
                sensors: this.selected
            })
        );
    }

    configureSensors(event) {
        this.onConfigureSensors(
            this.eventEmitter({
                event,
                sensors: this.selected
            })
        );
    }
    
    editLabels(event) {
        this.onEditLabels(
            this.eventEmitter({
                event,
                sensors: this.selected
            })
        );
    }

    changeActivationStatus(event) {
        this.onChangeActivationStatus(
            this.eventEmitter({
                event,
                sensors: this.selected
            })
        );
    }

    clearSelection() {
        this.selected = [];
    }
}
